/**
 * Story 061 — Edit-component modal (`/components?component=<uuid>`).
 *
 * R58-CC-02d: the read-only / role-gating UI is delegated to the shared
 * `<ReadOnlyModal>` primitive in `@aeriosiq/ui` + the
 * `useCanEditResource` hook. This file no longer hand-rolls the inline
 * banner, the per-input `slotProps.htmlInput.readOnly` plumbing, or the
 * local `MUTATE_ROLES` set.
 *
 * Behaviour preserved verbatim:
 *   - Loads `GET /components/{id}` on open.
 *   - Editable fields gated on `useCanEditResource(roles, 'component')`.
 *   - **Turbine (assetId) is editable for `tenant_admin` only** — Story
 *     061 §Open Q1 lock (mirrors Story 057's siteId narrowing). The
 *     project_manager / back_office `aria-disabled` tooltip path
 *     survives in editable mode.
 *   - Re-assigning Turbine triggers a confirmation dialog before PATCH.
 *   - Inactive components are read-only regardless of role; the
 *     primitive's `bannerOverride` carries the "Reactivate to edit." copy
 *     and `showActionsInReadOnly` keeps the Reactivate-component button
 *     reachable.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
  ReadOnlyModal,
  useCanEditResourceFor,
  useReadOnlyContext,
} from "@aeriosiq/ui";
import { useHasRole } from "@aeriosiq/auth-client";
import { useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../../admin/shared/ConfirmDialog.js";
import { ExternalIdsField } from "../../sites/shared/ExternalIdsField.js";
import {
  useDeactivateComponentMutation,
  useGetComponentQuery,
  useReactivateComponentMutation,
  useUpdateComponentMutation,
  type ComponentDetail,
  type ComponentExternalId,
  type UpdateComponentBody,
} from "../queries/index.js";
import {
  SiteAutocomplete,
  type SiteOption,
} from "../../turbines/shared/SiteAutocomplete.js";
import {
  TurbineAutocomplete,
  type TurbineOption,
} from "../shared/TurbineAutocomplete.js";
import { ComponentModelAutocomplete } from "../shared/ComponentModelAutocomplete.js";
import { useTurbinesListQuery } from "../../turbines/queries/index.js";
import { turbinesToOptions } from "../shared/TurbineAutocomplete.js";
import { useListComponentModelsLeanQuery } from "../../component-models/queries/hooks.js";

const STATUS_OPTIONS = [
  "ACTIVE",
  "SPARE",
  "RETIRED",
  "IN_TRANSIT",
  "IN_MAINTENANCE",
  "DECOMMISSIONED",
  "UNDEFINED",
] as const;

interface EditComponentFormValues {
  siteId: string | null;
  assetId: string | null;
  assetComponentModelId: string | null;
  serialNumber: string;
  manufactureDate: string;
  installedAt: string;
  lastMaintenanceAt: string;
  status: string;
  externalIds: ComponentExternalId[];
}

function detailToFormValues(
  d: ComponentDetail,
  resolvedSiteId: string | null,
): EditComponentFormValues {
  return {
    siteId: resolvedSiteId,
    assetId: d.assetId,
    assetComponentModelId: d.assetComponentModelId,
    serialNumber: d.serialNumber ?? "",
    manufactureDate: d.manufactureDate ?? "",
    installedAt: d.installedAt ?? "",
    lastMaintenanceAt: d.lastMaintenanceAt ?? "",
    status: d.status,
    externalIds: d.externalIds.map((e) => ({
      provider: e.provider,
      id: e.id,
    })),
  };
}

function dropEmptyExternalIds(
  arr: ComponentExternalId[],
): ComponentExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildUpdateBody(
  v: EditComponentFormValues,
  original: ComponentDetail,
  canMoveTurbine: boolean,
): UpdateComponentBody {
  const body: UpdateComponentBody = {
    serialNumber:
      v.serialNumber.trim().length > 0 ? v.serialNumber.trim() : null,
    manufactureDate:
      v.manufactureDate.trim().length > 0 ? v.manufactureDate.trim() : null,
    installedAt: v.installedAt.trim().length > 0 ? v.installedAt.trim() : null,
    lastMaintenanceAt:
      v.lastMaintenanceAt.trim().length > 0 ? v.lastMaintenanceAt.trim() : null,
    status: v.status.trim(),
    externalIds: dropEmptyExternalIds(v.externalIds),
  };
  if (
    v.assetComponentModelId !== null &&
    v.assetComponentModelId !== original.assetComponentModelId
  ) {
    body.assetComponentModelId = v.assetComponentModelId;
  }
  if (canMoveTurbine && v.assetId !== null && v.assetId !== original.assetId) {
    body.assetId = v.assetId;
  }
  return body;
}

function formatIso(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 19).replace("T", " ") + " UTC";
  } catch {
    return iso;
  }
}

export interface EditComponentDialogProps {
  componentId: string | null | undefined;
  onClose: () => void;
  /** All sites the actor can re-park to (active). */
  siteOptions: readonly SiteOption[];
}

export function EditComponentDialog({
  componentId,
  onClose,
  siteOptions,
}: EditComponentDialogProps): ReactElement {
  const open = typeof componentId === "string" && componentId.length > 0;
  const {
    data: detail,
    isPending,
    isError,
  } = useGetComponentQuery(componentId);

  // SB2-CC-01 — matrix-driven role gate (was R58-CC-02d's legacy
  // `useCanEditResource(roles, 'component')`).
  const canMutate = useCanEditResourceFor("component.update_tenant");
  // Cross-turbine move stays tenant_admin-only (Christian's lock).
  const canMoveTurbine = useHasRole("tenant_admin");

  // Resolve initial siteId — we can't infer it from the detail directly
  // (the wire shape doesn't carry parent siteId), so use the turbine
  // detail via the turbines list query. As a fallback we use null until
  // the user picks a Park.
  const [resolvedSiteId, setResolvedSiteId] = useState<string | null>(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, isSubmitting },
  } = useForm<EditComponentFormValues>({
    defaultValues: {
      siteId: null,
      assetId: null,
      assetComponentModelId: null,
      serialNumber: "",
      manufactureDate: "",
      installedAt: "",
      lastMaintenanceAt: "",
      status: "",
      externalIds: [],
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (detail) {
      reset(detailToFormValues(detail, resolvedSiteId));
    }
  }, [detail, reset, resolvedSiteId]);

  const update = useUpdateComponentMutation();
  const deactivate = useDeactivateComponentMutation();
  const reactivate = useReactivateComponentMutation();
  const { toast } = useToast();

  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  const [confirmReassign, setConfirmReassign] = useState<{
    fromLabel: string;
    toLabel: string;
    pendingValues: EditComponentFormValues;
  } | null>(null);

  const siteId = watch("siteId");

  // Cascading turbines query — for the Turbine picker. When siteId is
  // null we still need to surface the current parent turbine in the
  // dropdown, so we fall back to a single-page query without site
  // narrowing.
  const turbinesQuery = useTurbinesListQuery({
    page: 1,
    pageSize: 200,
    sort: "internalParkName:asc",
    ...(siteId ? { site: [siteId] } : {}),
  });
  const turbineOptions = useMemo<TurbineOption[]>(() => {
    if (!turbinesQuery.data) return [];
    return turbinesToOptions(turbinesQuery.data.items);
  }, [turbinesQuery.data]);

  // Cascading component-model query.
  const componentModelsQuery = useListComponentModelsLeanQuery();
  const componentModelOptions = useMemo(
    () => componentModelsQuery.data?.items ?? [],
    [componentModelsQuery.data?.items],
  );

  // Inactive components are read-only regardless of role.
  const isInactive = detail?.isActive === false;
  const readOnly = !canMutate || isInactive;

  function setSitePick(next: string | null): void {
    setResolvedSiteId(next);
    setValue("siteId", next, { shouldDirty: true });
    // Clear the Turbine pick when Park changes (mirror Add modal cascade).
    setValue("assetId", null, { shouldDirty: true });
  }

  function turbineLabelById(id: string | null): string {
    if (!id) return "—";
    const opt = turbineOptions.find((o) => o.id === id);
    if (opt) return opt.label;
    return detail?.assetConcatenatedName ?? "—";
  }

  async function performPatch(
    v: EditComponentFormValues,
    original: ComponentDetail,
  ): Promise<void> {
    try {
      await update.mutateAsync({
        id: original.id,
        body: buildUpdateBody(v, original, canMoveTurbine),
      });
      toast("Component updated.");
      onClose();
    } catch (err) {
      const errBody = (
        err as { body?: { code?: string; reason?: string; detail?: string } }
      ).body;
      if (errBody?.detail?.includes("forbidden_field")) {
        toast("Cannot set this field — it's managed by the system.", "error");
      } else if (
        errBody?.reason === "asset_id_mutation_requires_tenant_admin"
      ) {
        toast(
          "Only Tenant Admins can move a component between turbines.",
          "error",
        );
      } else {
        toast("Couldn't save component. Try again.", "error");
      }
    }
  }

  async function onSubmit(v: EditComponentFormValues): Promise<void> {
    if (!detail) return;
    if (canMoveTurbine && v.assetId !== null && v.assetId !== detail.assetId) {
      const fromLabel = detail.assetConcatenatedName;
      const toLabel = turbineLabelById(v.assetId);
      setConfirmReassign({
        fromLabel,
        toLabel,
        pendingValues: v,
      });
      return;
    }
    await performPatch(v, detail);
  }

  async function confirmReassignAction(): Promise<void> {
    if (!detail || confirmReassign === null) return;
    const pending = confirmReassign.pendingValues;
    setConfirmReassign(null);
    await performPatch(pending, detail);
  }

  async function doDeactivate(): Promise<void> {
    if (!detail) return;
    try {
      await deactivate.mutateAsync(detail.id);
      toast(`Component #${String(detail.componentCode)} deactivated.`);
      setConfirmDeactivate(false);
      onClose();
    } catch {
      toast("Couldn't deactivate component.", "error");
    }
  }

  async function doReactivate(): Promise<void> {
    if (!detail) return;
    try {
      await reactivate.mutateAsync(detail.id);
      toast(`Component #${String(detail.componentCode)} reactivated.`);
      setConfirmReactivate(false);
      onClose();
    } catch {
      toast("Couldn't reactivate component.", "error");
    }
  }

  const title =
    canMutate && detail?.isActive ? "Edit component" : "View component";

  const subtitle = useMemo(() => {
    if (!detail) return "";
    return `Component code ${String(detail.componentCode)} · ${detail.assetComponentModelName} · ${detail.assetConcatenatedName}`;
  }, [detail]);

  const titleNode = (
    <>
      <Typography
        id="edit-component-heading"
        component="span"
        variant="h6"
        sx={{ display: "block" }}
      >
        {title}
      </Typography>
      {detail ? (
        <Typography
          component="div"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </>
  );

  // Banner override — when the modal is read-only because the component
  // is inactive (rather than because the role lacks edit permission) we
  // surface the more informative "Reactivate to edit." copy.
  const bannerOverride =
    readOnly && isInactive
      ? "This component is inactive. Reactivate to edit."
      : undefined;

  const actionsNode = detail ? (
    <>
      <Button onClick={onClose} disabled={isSubmitting}>
        Cancel
      </Button>
      {!detail.isActive && canMutate ? (
        <Button
          variant="contained"
          onClick={() => {
            setConfirmReactivate(true);
          }}
          disabled={reactivate.isPending}
          startIcon={
            reactivate.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Reactivate component
        </Button>
      ) : null}
      {detail.isActive && canMutate ? (
        <>
          <Button
            color="error"
            onClick={() => {
              setConfirmDeactivate(true);
            }}
          >
            Deactivate component
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              void handleSubmit(onSubmit)();
            }}
            disabled={!isDirty || update.isPending}
            startIcon={
              update.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            Save changes
          </Button>
        </>
      ) : null}
    </>
  ) : null;

  return (
    <>
      <ReadOnlyModal
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-component-heading"
        readOnly={readOnly}
        entityNoun="component"
        {...(bannerOverride ? { bannerOverride } : {})}
        title={titleNode}
        actions={actionsNode}
        showActionsInReadOnly={readOnly && isInactive && canMutate}
      >
        {isPending ? (
          <Stack spacing={1.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : isError ? (
          <Alert severity="error">Couldn&apos;t load this component.</Alert>
        ) : (
          <Stack spacing={2}>
            {/* IDENTITY (read-only) */}
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Identity
            </Typography>
            <TextField
              label="Component ID"
              value={detail.id}
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Component ID",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />
            <TextField
              label="Component Code"
              value={String(detail.componentCode)}
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Component Code",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Model scope:
              </Typography>
              <Chip
                size="small"
                label={
                  detail.assetComponentModelScope === "global"
                    ? "Global"
                    : "Tenant"
                }
                variant="outlined"
              />
            </Stack>

            <Divider sx={{ my: 1 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Details
            </Typography>

            <Controller
              name="siteId"
              control={control}
              render={({ field }) => (
                <SiteAutocomplete
                  value={field.value}
                  options={siteOptions}
                  onChange={(next) => {
                    setSitePick(next);
                    field.onChange(next);
                  }}
                />
              )}
            />

            {/* Turbine — editable for tenant_admin only. For
                project_manager + back_office, render with
                `aria-disabled` + tooltip in editable mode. */}
            {canMutate && !canMoveTurbine && detail.isActive ? (
              <Tooltip title="Only Tenant Admins can move a component between turbines.">
                <Box>
                  <Controller
                    name="assetId"
                    control={control}
                    render={({ field }) => (
                      <TurbineAutocomplete
                        value={field.value}
                        options={turbineOptions}
                        onChange={field.onChange}
                        ariaDisabled
                      />
                    )}
                  />
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      fontSize: 12,
                      color: "text.secondary",
                      mt: 0.5,
                    }}
                  >
                    Only Tenant Admins can move a component between turbines.
                  </Box>
                </Box>
              </Tooltip>
            ) : (
              <Controller
                name="assetId"
                control={control}
                render={({ field }) => (
                  <TurbineAutocomplete
                    value={field.value}
                    options={turbineOptions}
                    loading={turbinesQuery.isPending}
                    onChange={field.onChange}
                  />
                )}
              />
            )}

            <Controller
              name="assetComponentModelId"
              control={control}
              render={({ field }) => (
                <ComponentModelAutocomplete
                  value={field.value}
                  options={componentModelOptions}
                  loading={componentModelsQuery.isPending}
                  onChange={field.onChange}
                />
              )}
            />

            <TextField
              {...register("serialNumber")}
              label="Serial Number"
              slotProps={{
                htmlInput: {
                  "aria-label": "Serial Number",
                  maxLength: 100,
                },
              }}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("manufactureDate")}
                label="Manufacture date"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    "aria-label": "Manufacture date",
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("installedAt")}
                label="Installed at"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    "aria-label": "Installed at",
                  },
                }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("lastMaintenanceAt")}
                label="Last maintenance at"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    "aria-label": "Last maintenance at",
                  },
                }}
                fullWidth
              />
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  // SB2-CC-03 — context-aware Status freeSolo select.
                  <StatusInlineSelect
                    value={field.value || ""}
                    onChange={(next) => {
                      field.onChange(next);
                    }}
                  />
                )}
              />
            </Stack>

            <ExternalIdsField
              control={control}
              register={register}
              readOnly={readOnly}
            />

            {/* AUDIT (read-only) */}
            <Divider sx={{ my: 1 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Audit
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Created at"
                value={formatIso(detail.createdAt)}
                variant="standard"
                slotProps={{
                  htmlInput: { readOnly: true, tabIndex: -1 },
                }}
                fullWidth
              />
              <AuditPersonField label="Created by" person={detail.createdBy} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Updated at"
                value={formatIso(detail.updatedAt)}
                variant="standard"
                slotProps={{
                  htmlInput: { readOnly: true, tabIndex: -1 },
                }}
                fullWidth
              />
              <AuditPersonField label="Updated by" person={detail.updatedBy} />
            </Stack>
            {detail.deletedAt ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Deleted at"
                  value={formatIso(detail.deletedAt)}
                  variant="standard"
                  slotProps={{
                    htmlInput: { readOnly: true, tabIndex: -1 },
                  }}
                  fullWidth
                />
                <AuditPersonField
                  label="Deleted by"
                  person={detail.deletedBy}
                />
              </Stack>
            ) : null}
          </Stack>
        )}
      </ReadOnlyModal>

      <ConfirmDialog
        open={confirmDeactivate}
        title={`Confirm: Deactivate component #${String(detail?.componentCode ?? "")}?`}
        description={
          <Box>
            Deactivating hides this component from the default list but
            historical references (defects, sub-components) stay intact. You can{" "}
            <strong>reactivate</strong> it later from the inactive view.
          </Box>
        }
        confirmLabel="Confirm"
        danger
        pending={deactivate.isPending}
        onConfirm={() => {
          void doDeactivate();
        }}
        onCancel={() => {
          setConfirmDeactivate(false);
        }}
      />

      <ConfirmDialog
        open={confirmReactivate}
        title={`Confirm: Reactivate component #${String(detail?.componentCode ?? "")}?`}
        description={
          <Box>
            This component will reappear in the default list and become
            available again for inspections and defects.
          </Box>
        }
        confirmLabel="Confirm"
        pending={reactivate.isPending}
        onConfirm={() => {
          void doReactivate();
        }}
        onCancel={() => {
          setConfirmReactivate(false);
        }}
      />

      <ConfirmDialog
        open={confirmReassign !== null}
        title={`Confirm: moving component #${String(detail?.componentCode ?? "")}`}
        description={
          confirmReassign === null ? null : (
            <Box>
              You are moving Component{" "}
              <strong>#{String(detail?.componentCode ?? "")}</strong> from
              turbine <strong>{confirmReassign.fromLabel}</strong> to{" "}
              <strong>{confirmReassign.toLabel}</strong>. Continue?
            </Box>
          )
        }
        confirmLabel="Confirm"
        pending={update.isPending}
        onConfirm={() => {
          void confirmReassignAction();
        }}
        onCancel={() => {
          setConfirmReassign(null);
        }}
      />
    </>
  );
}

/**
 * SB2-CC-03 — inline Status freeSolo Autocomplete that consumes the
 * `<ReadOnlyModal>` context to block popover opening in read-only mode.
 */
function StatusInlineSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}): ReactElement {
  const { readOnly } = useReadOnlyContext();
  return (
    <Autocomplete<string, false, false, true>
      freeSolo
      options={[...STATUS_OPTIONS]}
      value={value}
      readOnly={readOnly}
      onChange={(_e, next) => {
        onChange(next ?? "");
      }}
      onInputChange={(_e, next) => {
        onChange(next);
      }}
      renderInput={(params) => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            label="Status"
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": "Status",
              },
            }}
            fullWidth
          />
        );
        /* eslint-enable */
      }}
    />
  );
}

function AuditPersonField({
  label,
  person,
}: {
  label: string;
  person: { id: string; displayName: string | null } | null;
}): ReactElement {
  if (person?.displayName == null) {
    return (
      <TextField
        label={label}
        value="Unknown"
        variant="standard"
        slotProps={{
          htmlInput: { readOnly: true, tabIndex: -1 },
          input: { sx: { fontStyle: "italic" } },
        }}
        fullWidth
      />
    );
  }
  return (
    <TextField
      label={label}
      value={person.displayName}
      variant="standard"
      slotProps={{
        htmlInput: { readOnly: true, tabIndex: -1 },
      }}
      fullWidth
    />
  );
}
